import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AdminHeader from "../../Components/AdminHeader/AdminHeader";
import AdminNav from "../../Components/AdminNav/AdminNav";
import 'react-quill/dist/quill.snow.css';
import './news.css';
import { getNewsPostById, updateNewsPost } from "../../actions/NewsPages";
import { IMAGE_URL } from "../../path";
import EditCustomArticle from "./CustomArticle/EditCustomArticle";

interface BodyImage {
    id: number;
    file: File | null;
    caption: string;
    portrait: boolean;
    existingUrl?: string;
    removed?: boolean;
}

interface FormData {
    title: string;
    subtitle?: string;
    year_posted: string;
    image: File | null;
    video: string;
    content?: string;
    content_2?: string;
    template_id: number;
    [key: string]: any;
}

function EditNewsPost() {
    const location = useLocation();
    const { id, template } = useParams<{ id?: string, template?: string }>();
    const navigate = useNavigate();

    if (!location.state?.user) navigate('/login');

    const user = location.state?.user;

    const [isCollapsed, setIsCollapsed] = useState(true);
    const [errors, setErrors] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [formData, setFormData] = useState<FormData>({
        title: "",
        subtitle: "",
        year_posted: "",
        image: null,
        video: "",
        content: "",
        content_2: "",
        template_id: 1,
    });

    const [coverPhotoUrl, setCoverPhotoUrl] = useState<string>("");
    const [bodyImages, setBodyImages] = useState<BodyImage[]>([]);
    const [nextId, setNextId] = useState(-1);

    const ReactQuill = require('react-quill');
    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['link', 'image'],
            ['clean'],
        ]
    };

    /** ------------------- QUILL EMPTY CHECK ------------------- **/
    const isQuillEmpty = (html?: string) => {
        if (!html) return true;
        const stripped = html.replace(/<p>(&nbsp;|\s|<br>)*<\/p>/gi, "").trim();
        return stripped === "";
    };

    /** ------------------- FETCH EXISTING POST ------------------- **/
    useEffect(() => {
        const fetchNewsPost = async () => {
            if (!id) return;
            try {
                const response = await getNewsPostById(Number(id));
                const data = response?.data.data;
                if (data) {
                    setFormData(prev => ({
                        ...prev,
                        title: data.title || "",
                        subtitle: data.subtitle || "",
                        year_posted: data.year_posted || "",
                        image: null,
                        video: data.video_iframe || "",
                        content: data.content || "",
                        content_2: data.content_2 || "",
                        template_id: data.template_id || 1,
                    }));
                    if (data.image_path) setCoverPhotoUrl(IMAGE_URL + data.image_path);

                    const images: BodyImage[] = [];
                    for (let i = 1; i <= 27; i++) {
                        const url = data[`image_path_body_${i}`];
                        if (url) {
                            images.push({
                                id: i,
                                file: null,
                                caption: data[`image_path_body_${i}_caption`] || "",
                                portrait: data[`image_${i}_portrait`] === 1,
                                existingUrl: IMAGE_URL + url,
                                removed: false,
                            });
                        }
                    }
                    setBodyImages(images);
                }
            } catch (error) {
                console.error("Error fetching news post:", error);
                setErrors(["Failed to load news post."]);
            }
        };
        fetchNewsPost();
    }, [id]);

    if (template === "3") return <EditCustomArticle />;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === "file") {
            const file = (e.target as HTMLInputElement).files?.[0] || null;
            if (name === "image") {
                setFormData(prev => ({ ...prev, image: file }));
                setCoverPhotoUrl(file ? URL.createObjectURL(file) : "");
            } else {
                const bodyIndex = Number(name.replace("image_path_body_", ""));
                if (!isNaN(bodyIndex)) {
                    setBodyImages(prev =>
                        prev.map(img =>
                            img.id === bodyIndex ? { ...img, file, existingUrl: file ? URL.createObjectURL(file) : undefined, removed: false } : img
                        )
                    );
                }
            }
        } else if (name === "template_id") {
            setFormData(prev => ({ ...prev, template_id: Number(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleQuillChange = (value: string, name: string) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const updateBodyImageField = (id: number, caption?: string, portrait?: boolean) => {
        setBodyImages(prev =>
            prev.map(img => img.id === id ? { ...img, caption: caption ?? img.caption, portrait: portrait ?? img.portrait } : img)
        );
    };

    const removeBodyImage = (id: number) => {
        setBodyImages(prev =>
            prev.map(img => img.id === id ? { ...img, file: null, existingUrl: undefined, removed: true } : img)
        );
    };

    const addPortraitPair = () => {
        if (bodyImages.length + 2 > 27) return;
        setBodyImages(prev => [
            ...prev,
            { id: nextId, file: null, caption: "", portrait: true, removed: false },
            { id: nextId - 1, file: null, caption: "", portrait: true, removed: false }
        ]);
        setNextId(prev => prev - 2);
    };

    const addLandscapeImage = () => {
        if (bodyImages.length + 1 > 27) return;
        setBodyImages(prev => [
            ...prev,
            { id: nextId, file: null, caption: "", portrait: false, removed: false }
        ]);
        setNextId(prev => prev - 1);
    };

    /** ------------------- SUBMIT ------------------- **/
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setErrors([]);
        setSuccessMessage("");

        if (!formData.title.trim()) { setErrors(["Title is required"]); return; }
        if (!formData.year_posted) { setErrors(["Date is required"]); return; }

        try {
            const formDataToSend = new FormData();
            formDataToSend.append("title", formData.title);
            formDataToSend.append("year_posted", formData.year_posted);
            formDataToSend.append("template_id", formData.template_id.toString());

            if (formData.subtitle) formDataToSend.append("subtitle", formData.subtitle);
            if (!isQuillEmpty(formData.content)) formDataToSend.append("content", formData.content!);
            if (!isQuillEmpty(formData.content_2)) formDataToSend.append("content_2", formData.content_2!);
            if (formData.video) formDataToSend.append("video", formData.video);
            if (formData.image) formDataToSend.append("image", formData.image);
            else if (!coverPhotoUrl) formDataToSend.append("image", "");

            bodyImages.forEach((img, idx) => {
                const index = idx + 1;
                if (img.file) formDataToSend.append(`image_path_body_${index}`, img.file);
                else if (img.removed) formDataToSend.append(`image_path_body_${index}`, "");
                formDataToSend.append(`image_path_body_${index}_caption`, img.caption);
                formDataToSend.append(`image_${index}_portrait`, img.portrait ? "1" : "0");
            });

            const response = await updateNewsPost(formDataToSend, Number(id));
            if (response.data?.success) {
                setSuccessMessage("News post updated successfully!");
                navigate('/dashboard/news/list', { state: { user } });
            } else {
                setErrors([response.data?.message || "An unexpected error occurred"]);
            }
        } catch (error: any) {
            console.error("Error submitting form:", error);
            setErrors([error.message || "An error occurred while submitting the form"]);
        }
    };

    /** ------------------- TEMPLATE RENDERERS ------------------- **/
    const renderTemplate1 = () => (
        <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div>
                    <label>Cover Image</label><br />
                    <input type="file" name="image" onChange={handleInputChange} /><br />
                    {coverPhotoUrl && <img src={coverPhotoUrl} alt="Cover" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }} />}
                </div>
                <div>
                    <label>Video Embed</label>
                    <input type="text" name="video" value={formData.video} onChange={handleInputChange} className="form-control" />
                </div>
                <div>
                    <label>Quote</label>
                    <ReactQuill value={formData.content_2} onChange={(v: string) => handleQuillChange(v, 'content_2')} modules={modules} theme="snow" />
                </div>
            </div>
            <div style={{ flex: 1, display: 'flex', flexDirection: "column", gap: "15px" }}>
                <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="form-control" placeholder="Title *" required />
                <input type="date" name="year_posted" value={formData.year_posted} onChange={handleInputChange} className="form-control" required />
                <textarea name="subtitle" value={formData.subtitle} onChange={handleInputChange} className="form-control" placeholder="Subtitle" />
                <div>
                    <label>Content</label>
                    <ReactQuill value={formData.content} onChange={(v: string) => handleQuillChange(v, 'content')} modules={modules} theme="snow" />
                </div>
            </div>
        </div>
    );

    const renderTemplate2 = () => (
        <div>
            <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: "20px" }}>
                <div>
                    <label>Cover Image</label><br />
                    {coverPhotoUrl && <img src={coverPhotoUrl} alt="Cover" style={{ width: "80%", marginTop: "10px", borderRadius: "10px", objectFit: "cover" }} />} <br />
                    <input type="file" name="image" onChange={handleInputChange} className="form-control" />
                </div>
                <div style={{ width: "60%" }}>
                    <label>Video Embed</label>
                    <input type="text" name="video" value={formData.video} onChange={handleInputChange} className="form-control" />
                </div>
            </div>

            <div style={{ display: "flex", gap: "40px", alignItems: "flex-start", marginTop: "20px" }}>
                <div style={{ flex: 1 }}>
                    <label>Quote</label>
                    <ReactQuill value={formData.content_2} onChange={(v: string) => handleQuillChange(v, 'content_2')} modules={modules} theme="snow" />
                </div>
                <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "15px" }}>
                    <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="form-control" placeholder="Title *" required />
                    <input type="date" name="year_posted" value={formData.year_posted} onChange={handleInputChange} className="form-control" required />
                    <textarea name="subtitle" value={formData.subtitle} onChange={handleInputChange} className="form-control" placeholder="Subtitle" />
                    <div>
                        <label>Content</label>
                        <ReactQuill value={formData.content} onChange={(v: string) => handleQuillChange(v, 'content')} modules={modules} theme="snow" />
                    </div>
                </div>
            </div>
        </div>
    );

    /** ------------------- RENDER ------------------- **/
    return (
        <div>
            <AdminHeader isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} user={user} />
            <AdminNav collapsed={isCollapsed} setCollapsed={setIsCollapsed} user={user} />
            <div className={`main-admin-page ${isCollapsed ? "collapsed" : "expanded"}`}>
                <div className="row">
                    <div className="col-md-12">
                        <div className="x_panel">
                            <div className="x_title"><h2>Edit News Post</h2></div>
                            <div className="notification-box">
                                {errors.length > 0 && <div className="alert alert-danger"><ul>{errors.map((err, i) => <li key={i}>{err}</li>)}</ul></div>}
                                {successMessage && <div className="alert alert-success">{successMessage}</div>}
                            </div>
                            <div className="x_content">
                                <form onSubmit={handleSubmit} encType="multipart/form-data" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                                    <div>
                                        <label>Template</label>
                                        <select name="template_id" value={formData.template_id} onChange={handleInputChange} className="form-control">
                                            <option value={1}>Template 1</option>
                                            <option value={2}>Template 2</option>
                                        </select>
                                    </div>
                                    {formData.template_id === 1 ? renderTemplate1() : renderTemplate2()}

                                    {/* Body Images */}
                                    <div>
                                        <div style={{ display: "flex", gap: "10px", margin: "15px 0" }}>
                                            <button type="button" className="btn btn-primary" onClick={addPortraitPair} disabled={bodyImages.length >= 27}>Add Portrait Pair</button>
                                            <button type="button" className="btn btn-secondary" onClick={addLandscapeImage} disabled={bodyImages.length >= 27}>Add Landscape</button>
                                        </div>
                                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))", gap: "15px" }}>
                                            {bodyImages.map((img, idx) => !img.removed && (
                                                <div key={img.id} style={{ border: "1px solid #ccc", padding: "10px", position: "relative" }}>
                                                    <label>Body Image {idx + 1}</label>
                                                    <input type="file" name={`image_path_body_${img.id}`} className="form-control" onChange={handleInputChange} />
                                                    <input type="text" className="form-control" placeholder="Caption" value={img.caption} onChange={e => updateBodyImageField(img.id, e.target.value)} />
                                                    <label><input type="checkbox" checked={img.portrait} onChange={e => updateBodyImageField(img.id, undefined, e.target.checked)} /> Portrait</label>
                                                    <button type="button" className="btn btn-danger btn-sm" style={{ position: "absolute", top: 5, right: 5 }} onClick={() => removeBodyImage(img.id)}>Remove</button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                                        <button type="submit" className="btn btn-success">Update Post</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditNewsPost;
